import { useState, useRef, useEffect } from 'react';
import { encodeWAV, playUserRecordingWithVoiceChanger } from '../utils/audioUtils';
import api from '../../../services/api';

export const useVirtualClassAudio = ({ 
  page, 
  lesson, 
  selectedFlashcard, 
  exerciseType = 'see',
  maxRecordingTime = 10,
  onResult,
  getAttemptNumber,
  skipApiCall = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlayingReminder, setIsPlayingReminder] = useState(false);
  
  const recordingTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const isRecordingRef = useRef(false);
  const userRecordingRef = useRef(null);
  const reminderAudioRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxRecordingTime - 1) {
            handleSubmitRecording();
            return maxRecordingTime - 1;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording, maxRecordingTime]);

  useEffect(() => {
    return () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (userRecordingRef.current) {
        URL.revokeObjectURL(userRecordingRef.current);
      }
      if (reminderAudioRef.current) {
        reminderAudioRef.current.pause();
      }
    };
  }, []);

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          throw new Error('Web Audio API not supported');
        }
        
        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;
        
        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        
        audioChunksRef.current = [];
        
        processor.onaudioprocess = (e) => {
          if (!isRecordingRef.current) return;
          const inputData = e.inputBuffer.getChannelData(0);
          const samples = new Float32Array(inputData.length);
          samples.set(inputData);
          audioChunksRef.current.push(samples);
        };
        
        source.connect(processor);
        processor.connect(audioContext.destination);
        
        mediaRecorderRef.current = { processor, source, audioContext };
        isRecordingRef.current = true;
        setIsRecording(true);
        setRecordingTime(0);
        
      } catch (webAudioError) {
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };
        
        mediaRecorder.start(100);
        isRecordingRef.current = true;
        setIsRecording(true);
        setRecordingTime(0);
      }
    } catch (error) {
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const handleSubmitRecording = async () => {
    isRecordingRef.current = false;
    setIsRecording(false);
    
    let audioBlob;
    let isWavFormat = false;
    
    const isWebAudio = mediaRecorderRef.current && mediaRecorderRef.current.processor;
    
    if (isWebAudio) {
      const { processor, source } = mediaRecorderRef.current;
      if (processor) processor.disconnect();
      if (source) source.disconnect();
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }

      await new Promise(resolve => setTimeout(resolve, 200));

      if (audioChunksRef.current.length === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }

      const totalLength = audioChunksRef.current.reduce((sum, chunk) => sum + (chunk.length || 0), 0);
      if (totalLength === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }
      
      const combinedSamples = new Float32Array(totalLength);
      let offset = 0;
      for (const chunk of audioChunksRef.current) {
        if (chunk && chunk.length > 0) {
          combinedSamples.set(chunk, offset);
          offset += chunk.length;
        }
      }

      const sampleRate = audioContextRef.current?.sampleRate || 16000;
      const wavBuffer = encodeWAV(combinedSamples, sampleRate, 1);
      audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      isWavFormat = true;
      
    } else {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        const stopPromise = new Promise((resolve) => {
          if (mediaRecorderRef.current) {
            const originalOnStop = mediaRecorderRef.current.onstop;
            mediaRecorderRef.current.onstop = () => {
              if (originalOnStop) originalOnStop();
              setTimeout(() => resolve(), 100);
            };
          } else {
            resolve();
          }
        });

        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
        }
        mediaRecorderRef.current.stop();
        await stopPromise;
      }
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }

      if (audioChunksRef.current.length === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }

      const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext({ sampleRate: 16000 });
        
        const arrayBuffer = await webmBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        const samples = audioBuffer.getChannelData(0);
        const sampleRate = audioBuffer.sampleRate;
        
        const wavBuffer = encodeWAV(samples, sampleRate, 1);
        audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
        isWavFormat = true;
        
        await audioContext.close();
      } catch (conversionError) {
        audioBlob = webmBlob;
        isWavFormat = false;
      }
    }

    setIsProcessing(true);

    const userRecordingUrl = URL.createObjectURL(audioBlob);
    userRecordingRef.current = userRecordingUrl;

    if (skipApiCall) {
      setIsProcessing(false);
      
      if (onResult) {
        onResult({ skipped: true }, userRecordingUrl);
      }
      return { skipped: true };
    }

    try {
      const lessonId = lesson?.databaseId || lesson?.id || page.lessonId;
      if (!lessonId) {
        throw new Error('Lesson ID is required');
      }

      if (!selectedFlashcard) {
        throw new Error('No flashcard selected');
      }

      const flashcardId = selectedFlashcard.id;
      
      const currentAttempt = getAttemptNumber ? getAttemptNumber(flashcardId) : 0;
      const attemptNumber = currentAttempt + 1;
      
      const formData = new FormData();
      const filename = isWavFormat ? 'recording.wav' : 'recording.webm';
      formData.append('audio', audioBlob, filename);
      formData.append('exerciseType', exerciseType);
      formData.append('flashcardId', flashcardId);
      formData.append('pageId', page.id);
      formData.append('expectedWord', selectedFlashcard.word);
      formData.append('attemptNumber', attemptNumber.toString()); // Send attempt number to backend

      const response = await api.post(`/lessons/${lessonId}/exercise-submit`, formData);
      const result = response.data.data;

      setIsProcessing(false);

      if (onResult) {
        onResult(result, userRecordingUrl);
      }

      return result;
    } catch (error) {
      console.error('Error submitting recording:', error);
      setIsProcessing(false);
      alert('Error processing audio. Please try again.');
      throw error;
    }
  };

  const playReminderAudio = (reminderAudio, onEnded) => {
    try {
      let audioUrl;
      
      if (typeof reminderAudio === 'string') {
        if (reminderAudio.startsWith('http://') || reminderAudio.startsWith('https://')) {
          audioUrl = reminderAudio;
        } else {
          if (onEnded) onEnded();
          return;
        }
      } else if (reminderAudio?.url) {
        audioUrl = reminderAudio.url;
      } else {
        if (onEnded) onEnded();
        return;
      }
      
      if (reminderAudioRef.current) {
        reminderAudioRef.current.pause();
        reminderAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      reminderAudioRef.current = audio;
      
      audio.onended = () => {
        setIsPlayingReminder(false);
        if (onEnded) onEnded();
      };
      
      audio.onerror = () => {
        setIsPlayingReminder(false);
        if (onEnded) onEnded();
      };
      
      setIsPlayingReminder(true);
      audio.play().catch(error => {
        console.error('Error starting reminder audio playback:', error);
        setIsPlayingReminder(false);
        if (onEnded) onEnded();
      });
    } catch (error) {
      console.error('Error playing reminder audio:', error);
      setIsPlayingReminder(false);
      if (onEnded) onEnded();
    }
  };

  return {
    isRecording,
    recordingTime,
    isProcessing,
    isPlayingReminder,
    userRecordingUrl: userRecordingRef.current,
    handleStartRecording,
    handleSubmitRecording,
    playUserRecording: (onEnded) => playUserRecordingWithVoiceChanger(userRecordingRef.current, onEnded),
    playReminderAudio
  };
};

